import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import GroupIcon from '@mui/icons-material/Group';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import useAuth from '../hooks/useAuth';
import StatusChip from '../components/common/StatusChip';
import { getDashboardStats, getRecentActivity, getAtRiskStudents } from '../services/collegeApi';
import { relativeTime, formatPercent } from '../utils/formatters';
import { ROLES } from '../utils/constants';

export default function DashboardPage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [atRisk, setAtRisk] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const canViewStudents = role === ROLES.ADMIN || role === ROLES.FACULTY;

  useEffect(() => {
    if (!user?.college_id) return;
    setLoading(true);
    Promise.all([
      getDashboardStats(user.college_id).catch(() => null),
      getRecentActivity(user.college_id, 8).catch(() => ({ activity: [] })),
      canViewStudents ? getAtRiskStudents(user.college_id, 5).catch(() => ({ students: [] }))
                      : Promise.resolve({ students: [] }),
    ])
      .then(([s, a, r]) => {
        setStats(s);
        setActivity(a?.activity || []);
        setAtRisk(r?.students || []);
      })
      .catch((err) => setError(err?.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [user, canViewStudents]);

  const onTrackPct = stats
    ? formatPercent(stats.on_track_count || 0, stats.active_students || 0)
    : 0;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        {user?.college_name ? `${user.college_name} Dashboard` : 'Dashboard'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Cohort progress, credit completion, and student status at a glance.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <StatCard
              icon={<GroupIcon sx={{ fontSize: 32, color: '#2563eb' }} />}
              label="Active Students"
              value={stats?.active_students ?? 0}
              sublabel={stats?.active_cohorts ? `${stats.active_cohorts} active cohort${stats.active_cohorts === 1 ? '' : 's'}` : null}
              color="#2563eb"
            />
            <StatCard
              icon={<TrendingUpIcon sx={{ fontSize: 32, color: '#16a34a' }} />}
              label="On-Track Pace"
              value={`${onTrackPct}%`}
              sublabel={stats ? `${stats.on_track_count || 0} of ${stats.active_students || 0}` : null}
              color="#16a34a"
            />
            <StatCard
              icon={<AssessmentIcon sx={{ fontSize: 32, color: '#7c3aed' }} />}
              label="Assessments Completed"
              value={stats?.assessments_completed ?? 0}
              sublabel={stats?.assessments_this_semester != null ? `${stats.assessments_this_semester} this semester` : null}
              color="#7c3aed"
            />
            <StatCard
              icon={<WarningAmberIcon sx={{ fontSize: 32, color: '#dc2626' }} />}
              label="Need Attention"
              value={stats?.at_risk_count ?? 0}
              sublabel="Below pace or stalled"
              color="#dc2626"
              onClick={canViewStudents ? () => navigate('/students?status=at_risk') : undefined}
            />
          </Grid>

          <Grid container spacing={3}>
            {/* At-Risk Students */}
            {canViewStudents && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        At-Risk Students
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Behind credit pace — review and reach out
                      </Typography>
                    </Box>
                    {atRisk.length > 0 && (
                      <Button
                        size="small"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => navigate('/students?status=at_risk')}
                      >
                        View all
                      </Button>
                    )}
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  {atRisk.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography variant="body2">No at-risk students. Nice work.</Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {atRisk.map((s) => (
                        <ListItem
                          key={s.student_id || s.enrollment_id}
                          button
                          onClick={() => navigate(`/credits?student=${s.student_id}`)}
                          sx={{ px: 1, borderRadius: 1, '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                          <ListItemText
                            primary={s.name || s.email}
                            secondary={s.cohort_name ? `${s.cohort_name} · ${s.progress_pct ?? 0}% complete` : `${s.progress_pct ?? 0}% complete`}
                            primaryTypographyProps={{ fontWeight: 600 }}
                          />
                          <StatusChip status="at_risk" />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>
            )}

            {/* Recent Activity */}
            <Grid item xs={12} md={canViewStudents ? 6 : 12}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Recent Activity</Typography>
                <Typography variant="body2" color="text.secondary">
                  Assessments, mentor approvals, and enrollments
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                {activity.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">No recent activity.</Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {activity.map((evt, idx) => (
                      <ListItem key={evt.id || idx} sx={{ px: 1 }}>
                        <ListItemText
                          primary={evt.message || evt.title || evt.type}
                          secondary={`${evt.actor || evt.student_name || ''}${evt.actor && evt.timestamp ? ' · ' : ''}${evt.timestamp ? relativeTime(evt.timestamp) : ''}`}
                          primaryTypographyProps={{ fontSize: '0.92rem' }}
                          secondaryTypographyProps={{ fontSize: '0.8rem' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

function StatCard({ icon, label, value, sublabel, color, onClick }) {
  return (
    <Grid item xs={12} sm={6} md={6} lg={3}>
      <Paper
        onClick={onClick}
        sx={{
          p: { xs: 2, sm: 2.5 },
          overflow: 'hidden',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'transform 0.15s, box-shadow 0.15s',
          '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' } : {},
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{ flexShrink: 0 }}>{icon}</Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" color="text.secondary" noWrap>{label}</Typography>
            <Typography
              sx={{
                fontWeight: 700,
                color,
                fontSize: 'clamp(1.05rem, 4vw, 2rem)',
                lineHeight: 1.2,
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
              title={String(value ?? '—')}
            >
              {value ?? '—'}
            </Typography>
            {sublabel && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }} noWrap>
                {sublabel}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
}
